// src/pages/reports/UniqueActivateSubscriberStbReports.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField, MenuItem, Select, InputLabel, FormControl, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { get } from 'src/services/httpClient.service';
import { authenticationService } from 'src/services/authentication.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

const statusOptions = [
  { value: '', label: 'All'},
  { value: 'ACTIVE', label: 'ACTIVE' },
  { value: 'INACTIVE', label: 'INACTIVE' },
  { value: 'INSTOCK', label: 'INSTOCK' },
  { value: 'DAMAGED', label: 'DAMAGED' },
  { value: 'REPAIRED', label: 'REPAIRED' },
  { value: 'REPLACED', label: 'REPLACED' },
  { value: 'BLACKLIST', label: 'BLACKLIST' },
  { value: 'FAULTY', label: 'FAULTY' },
  { value: 'SUSPEND', label: 'SUSPEND' },
];

// Define columns for the report download service
const columns = [
  // { field: 'userId', headerName: 'User ID', minWidth: 100 },
  { field: 'customerId', headerName: 'Customer ID', minWidth: 120 },
  { field: 'email', headerName: 'User Name', minWidth: 250 },
  { field: 'devices', headerName: 'STB Number', minWidth: 300 },
  { field: 'userStatus', headerName: 'User Status', minWidth: 100 },
  { field: 'deviceStatus', headerName: 'Device Status', minWidth: 100 },
  { field: 'fullBillingAddress', headerName: 'Billing Address', minWidth: 250 },
  { field: 'fullInstallationAddress', headerName: 'Installation Address', minWidth: 250 },
  { field: 'deviceUpdatedDateStr', headerName: 'Device Updated Date', minWidth: 170 }, // Slightly wider for DD/MM/YYYY format
];

const UniqueActivateSubscriberStbReports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchKey, setSearchKey] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const [selectedStatus, setSelectedStatus] = useState('');
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  const fetchData = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      const from = dayjs(startDate).format('YYYY-MM-DD');
      const to = dayjs(endDate).format('YYYY-MM-DD');

      const response = await get(
        `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/getSTBStatusReport?status=${selectedStatus}&fromDate=${from}&toDate=${to}&searchKey=${searchKey}`
      );

       const dataWithId = response?.data.map((item: any, index: number) => ({
      id: index + 1,
      deviceUpdatedDateStr: item.deviceUpdatedDate ? dayjs(item.deviceUpdatedDate).format('DD/MM/YYYY HH:mm:ss') : '',
      // Ensure customerId is available, fallback to userId if needed
      customerId: item.customerId || (item.userId ? String(item.userId) : ''),
      ...item,
    }));

      setRows(dataWithId);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    fetchData();
  };

  useEffect(() => {
    handleSubmit();
  }, []);

  const generateTimestampedFilename = (baseName: string, status: string, start: Dayjs | null, end: Dayjs | null) => {
    const from = start ? dayjs(start).format('YYYY-MM-DD') : 'NA';
    const to = end ? dayjs(end).format('YYYY-MM-DD') : 'NA';
    const time = dayjs().format('HH-mm-ss');
    const statusSuffix = status ? `_${status}` : '_ALL';
    return `${baseName}${statusSuffix}_${from}_to_${to}_${time}`;
  };

  // Keep your original Excel logic with loading state
  // Update handleDownloadExcel method
const handleDownloadExcel = () => {
  setDownloading(prev => ({ ...prev, excel: true }));
  
  try {
    // Update headers to include Customer ID instead of User ID
    const headers = [
      'Customer ID', // Changed from 'User ID'
      'User Name',
      'STB Number',
      'User Status',
      'Device Status',
      'Billing Address',
      'Installation Address',
      'Device Updated Date'
    ];

    const data = filteredRows.map((row: any) => [
      row.customerId || '', // Changed from row.userId
      row.email || '',
      row.devices || '',
      row.userStatus || '',
      row.deviceStatus || '',
      row.fullBillingAddress || '',
      row.fullInstallationAddress || '',
      row.deviceUpdatedDateStr || ''
    ]);

    const finalData = [headers, ...data];

    const worksheet = XLSX.utils.aoa_to_sheet(finalData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'STB Status Report');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const fileName = generateTimestampedFilename('Unique Active Subscriber Stb Report', selectedStatus, startDate, endDate);
    saveAs(blob, `${fileName}.xlsx`);
  } catch (error) {
    console.error('Error generating Excel file:', error);
  } finally {
    setDownloading(prev => ({ ...prev, excel: false }));
  }
};


  // Single download handler for PDF using common service (no pdfOptions)
 // Update handleDownloadPDF method
const handleDownloadPDF = async () => {
  if (!startDate || !endDate) {
    alert('Please select both start and end dates');
    return;
  }

  setDownloading(prev => ({ ...prev, pdf: true }));
  
  try {
    const fromDate = startDate.format('DD/MM/YYYY');
    const toDate = endDate.format('DD/MM/YYYY');
    const fromStr = startDate.format('YYYY-MM-DD');
    const toStr = endDate.format('YYYY-MM-DD');
    const statusFilterText = selectedStatus || 'All Statuses';
    const searchFilterText = searchKey || 'No Search Filter';
    
    // Generate custom filename
    const statusSuffix = selectedStatus ? `_${selectedStatus}` : '_ALL';
    const customFileName = `Unique_Active_Subscriber_STB_Report${statusSuffix}_${fromStr}_to_${toStr}_${dayjs().format('HHmmss')}.pdf`;
    
    // Get status distribution statistics
    const statusDistribution: Record<string, number> = {};
    filteredRows.forEach((row: any) => {
      const status = row.deviceStatus || 'Unknown';
      statusDistribution[status] = (statusDistribution[status] || 0) + 1;
    });

    // Update the columns for PDF - remove userId, add customerId
    const pdfColumns = [
      { field: 'customerId', headerName: 'Customer ID', minWidth: 120 },
      { field: 'email', headerName: 'User Name', minWidth: 250 },
      { field: 'devices', headerName: 'STB Number', minWidth: 300 },
      { field: 'userStatus', headerName: 'User Status', minWidth: 100 },
      { field: 'deviceStatus', headerName: 'Device Status', minWidth: 100 },
      { field: 'fullBillingAddress', headerName: 'Billing Address', minWidth: 250 },
      { field: 'fullInstallationAddress', headerName: 'Installation Address', minWidth: 250 },
      { field: 'deviceUpdatedDateStr', headerName: 'Device Updated Date', minWidth: 170 },
    ];

    const success = await reportDownloadService.downloadReport({
      title: `Unique Active Subscriber STB Report${selectedStatus ? ` - ${selectedStatus}` : ''}`,
      columns: pdfColumns,
      rows: filteredRows,
      month: startDate.month() + 1,
      year: startDate.year(),
      fileName: customFileName,
    }, 'pdf');
    
    if (!success) {
      alert('Failed to generate PDF. Please try again.');
    }
  } catch (error) {
    console.error('Error downloading PDF:', error);
    alert('Error generating PDF. Please try again.');
  } finally {
    setDownloading(prev => ({ ...prev, pdf: false }));
  }
};

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchKey.toLowerCase())
  ));

  return (
    <>
      <Helmet>
        <title>Unique Active Subscriber STB Reports</title>
      </Helmet>

      <Box sx={{ width: "100%", paddingX: '20px' }}>
        <PageTitleWrapper>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
              <PageTitle
                heading={`Unique Active Subscriber STB Reports`}
                subHeading={`Details of Subscriber & STB`}
                handleClick={undefined}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasPermission(["OWNER"]) && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadExcel}
                    disabled={downloading.excel}
                  >
                    {downloading.excel ? 'Generating...' : 'Download Excel'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleDownloadPDF}
                    disabled={downloading.pdf}
                  >
                    {downloading.pdf ? 'Generating...' : 'Download PDF'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </PageTitleWrapper>
      </Box>

      {/* FILTER SECTION */}
      <Box sx={{ px: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    🔍
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            
            <DatePicker
              label="Start Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
            <DatePicker
              label="End Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ height: '40px', minWidth: '100px' }}
            >
              {loading ? 'Loading...' : 'Submit'}
            </Button>
          </Stack>
        </LocalizationProvider>
      </Box>

      {/* DATAGRID SECTION */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              paddingTop: '12px',
              paddingBottom: '12px',
            },
            '& .MuiDataGrid-cell:hover': {
              color: 'rgb(43 44 45)',
            },
          }}
        />
      </Box>
    </>
  );
};

export default UniqueActivateSubscriberStbReports;